import * as Dialog from '@radix-ui/react-dialog';
import * as Sentry from '@sentry/react';
import React, { ReactNode } from 'react';
import { Body } from '../layout/body';
import { Footer } from '../layout/footer';
import { Header } from '../layout/header';
import { HeaderText } from '../typography/header';
import { MODAL_CONTAINER_ID } from './modal-container';
import styles from './modal.module.scss';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

function ModalErrorBoundary({ children }: { children: ReactNode }) {
	return (
		<Sentry.ErrorBoundary
			fallback={() => <></>}
			beforeCapture={(scope) => {
				scope.setTag('location', 'Modal');
			}}
		>
			{children}
		</Sentry.ErrorBoundary>
	);
}

export interface ModalControlProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

interface ModalProps extends ModalControlProps {
	children: React.ReactNode;
}

interface ModalWithTriggerProps {
	trigger: React.ReactNode;
	children: React.ReactNode;
}

export function Modal({ open, onOpenChange, children }: ModalProps) {
	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal container={document.getElementById(MODAL_CONTAINER_ID)}>
				<Dialog.Overlay className={styles.Overlay} />
				<Dialog.Content className={styles.Content}>
					<ModalErrorBoundary>{children}</ModalErrorBoundary>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

export function ModalWithTrigger({ trigger, children }: ModalWithTriggerProps) {
	return (
		<Dialog.Root>
			<Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
			<Dialog.Portal container={document.getElementById(MODAL_CONTAINER_ID)}>
				<Dialog.Overlay
					className={styles.Overlay}
					onClick={(e) => e.stopPropagation()}
				/>
				<Dialog.Content
					className={styles.Content}
					onClick={(e) => e.stopPropagation()}
				>
					<ModalErrorBoundary>{children}</ModalErrorBoundary>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}

function ModalHeader({ children, closeModal }: { children: ReactNode, closeModal?: () => void }) {
	return (
		<Header closeModal={closeModal}>
			<VisuallyHidden>
				<Dialog.Title>{children}</Dialog.Title>
			</VisuallyHidden>
			<HeaderText>{children}</HeaderText>
		</Header>
	);
}

function ModalBody({ children }: { children: ReactNode }) {
	return (
		<Body>
			<VisuallyHidden>
				<Dialog.Description asChild>
					<span>{children}</span>
				</Dialog.Description>
			</VisuallyHidden>
			{children}
		</Body>
	);
}

function ModalFooter({ children }: { children: ReactNode }) {
	return <Footer>{children}</Footer>;
}

function ModalClose({ children }: { children: ReactNode }) {
	return <Dialog.Close asChild>{children}</Dialog.Close>;
}

Modal.Button = Dialog.Trigger;
Modal.Close = Dialog.Close;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Close = ModalClose;
